import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { 
  Server, Wifi, Network, Wrench, Clock, DollarSign, ChevronDown, ChevronUp,
  Settings, FileText, Users, Printer, DollarSign as DollarIcon, BarChart3,
  CheckCircle, Edit, Upload, X, AlertCircle, Sun, Moon, User, LogOut
} from 'lucide-react';
import AnnouncementPopup from '../components/AnnouncementPopup';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [licenseData, setLicenseData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [activating, setActivating] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [hasPendingPayment, setHasPendingPayment] = useState(false);
  
  useEffect(() => {
    loadData();
    loadAnnouncements();
  }, []);
  
  const loadData = async () => {
    try {
      const response = await api.get('/api/license/current-pricing');
      setLicenseData(response.data);
      
      const activeModules = response.data.active_modules || [];
      const expanded = {};
      activeModules.forEach(mod => {
        expanded[mod] = true;
      });
      setExpandedSections(expanded);
      
      // Verificar si tiene payment_request pendiente
      try {
        const paymentsRes = await api.get('/api/payment-requests');
        const requests = paymentsRes.data.requests || [];
        const pendingPayments = requests.filter(p => p.status === 'pending');
        setHasPendingPayment(pendingPayments.length > 0);
      } catch (err) {
        console.log('No se pudo verificar payment requests');
      }
      
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadAnnouncements = async () => {
    try {
      const response = await api.get('/api/admin/announcements/active');
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error cargando anuncios:', error);
    }
  };
  
  const handleDismissAnnouncement = async () => {
    const announcement = announcements[currentAnnouncementIndex];
    if (!announcement) return;
    
    try {
      await api.post(`/api/admin/announcements/${announcement.id}/dismiss`);
      
      if (currentAnnouncementIndex < announcements.length - 1) {
        setCurrentAnnouncementIndex(currentAnnouncementIndex + 1);
      } else {
        setAnnouncements([]);
      }
    } catch (error) {
      console.error('Error:', error);
      if (currentAnnouncementIndex < announcements.length - 1) {
        setCurrentAnnouncementIndex(currentAnnouncementIndex + 1);
      } else {
        setAnnouncements([]);
      }
    }
  };
  
  const toggleSection = (moduleId) => {
    setExpandedSections(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };
  
  const handleModuleClick = (module) => {
    const isActive = licenseData?.active_modules?.includes(module.id);
    const hasTools = licenseData?.tools_access;
    
    // Obtener precio dinámico desde licenseData.pricing
    const dynamicPrice = licenseData?.pricing?.[module.id] || module.basePrice;
    const moduleWithPrice = { ...module, basePrice: dynamicPrice };
    
    if (module.id === 'tools') {
      if (hasTools) {
        toggleSection('tools');
      } else {
        setSelectedModule(moduleWithPrice);
        setShowModal(true);
      }
      return;
    }
    
    if (isActive) {
      toggleSection(module.id);
    } else {
      setSelectedModule(moduleWithPrice);
      setShowModal(true);
    }
  };
  
  const handleActivate = async () => {
    try {
      setActivating(true);
      
      if (selectedModule.id === 'tools') {
        navigate('/tools-purchase');
        setShowModal(false);
        return;
      }
      
      await api.post('/api/modules/activate', {
        module_name: selectedModule.id
      });
      
      await loadData();
      setShowModal(false);
      setExpandedSections(prev => ({ ...prev, [selectedModule.id]: true }));
      
    } catch (error) {
      alert(error.response?.data?.detail || 'Error activando módulo');
    } finally {
      setActivating(false);
    }
  };
  
  const isActive = (moduleId) => {
    if (moduleId === 'tools') return licenseData?.tools_access;
    return licenseData?.active_modules?.includes(moduleId);
  };
  
  const isExpanded = (moduleId) => {
    return expandedSections[moduleId] || false;
  };
  
  const isTrialExpired = licenseData?.days_remaining <= 0;
  
  if (isTrialExpired) {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-2xl w-full p-8`}>
          
          {hasPendingPayment ? (
            // Mensaje cuando ya reportó el pago
            <>
              <div className="text-center mb-6">
                <div className="bg-yellow-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-12 h-12 text-white" />
                </div>
                <h2 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Esperando aprobación del pago
                </h2>
                <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Tu pago está en revisión. Te notificaremos cuando sea aprobado.
                </p>
              </div>
              
              <div className={`rounded-lg p-6 mb-6 ${isDark ? 'bg-gray-700' : 'bg-yellow-50'}`}>
                <p className="text-center text-sm">
                  ⏳ El administrador revisará tu comprobante pronto.<br/>
                  Recibirás acceso una vez aprobado.
                </p>
              </div>
            </>
          ) : (
            // Mensaje cuando NO ha reportado pago (original)
            <>
              <div className="text-center mb-6">
                <div className="bg-red-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-12 h-12 text-white" />
                </div>
                <h2 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {licenseData?.status === 'trial' ? 'Tu período de prueba terminó' : 'Tu plan ha vencido'}
                </h2>
                <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Para continuar usando el sistema, elige tu plan de pago
                </p>
              </div>
              
              <div className={`rounded-lg p-6 mb-6 ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <h3 className="font-bold mb-3">📊 Resumen de tu uso:</h3>
                <div className="space-y-2">
                  {(licenseData?.active_modules || []).map((mod, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>{mod.toUpperCase()}</span>
                    </div>
                  ))}
                  <div className="pt-3 border-t border-gray-300 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-bold">Total mensual:</span>
                      <span className="text-2xl font-bold text-blue-600">
                        ${licenseData?.pricing?.final_monthly_total || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => navigate('/payment-summary')}
                className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg font-bold text-xl hover:bg-blue-700"
              >
                {licenseData?.billing_cycle ? 'Reportar Pago →' : 'Elegir Plan y Pagar →'}
              </button>
            </>
          )}
          
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  const modules = [
    { id: 'vpn', name: 'VPN', basePrice: 50, icon: Server, gradient: 'from-blue-500 to-cyan-600', borderColor: 'border-blue-500', bgColor: 'bg-blue-500', buttonColor: 'from-blue-600 to-cyan-600' },
    { id: 'hotspot', name: 'Hotspot', basePrice: 299, icon: Wifi, gradient: 'from-purple-500 to-pink-600', borderColor: 'border-purple-500', bgColor: 'bg-purple-500', buttonColor: 'from-purple-600 to-pink-600' },
    { id: 'pppoe', name: 'PPPoE', basePrice: 299, icon: Network, gradient: 'from-green-500 to-emerald-600', borderColor: 'border-green-500', bgColor: 'bg-green-500', buttonColor: 'from-green-600 to-emerald-600' },
    { id: 'tools', name: 'Herramientas', basePrice: 999, icon: Wrench, gradient: 'from-orange-500 to-red-600', borderColor: 'border-orange-500', bgColor: 'bg-orange-500', buttonColor: 'from-orange-600 to-red-600', isOneTime: true } // Precio default, se actualiza con licenseData
  ];
  
  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Navbar Superior */}
      <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b shadow-md`}>
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-2 rounded-lg">
                <Server className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  EasyTickets
                </h1>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {user?.email}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                title="Cambiar tema"
              >
                {isDark ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-gray-600" />}
              </button>
              
              <button
                onClick={() => navigate('/profile')}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                title="Mi perfil"
              >
                <User className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
              
              <button
                onClick={logout}
                className="px-4 py-2 rounded-lg font-semibold bg-red-500 hover:bg-red-600 text-white flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Salir
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contenido */}
      <div className="max-w-6xl mx-auto p-6">
        {/* Banner dinámico según status */}
        <div className={`bg-gradient-to-r ${
          licenseData?.status === 'trial' 
            ? 'from-blue-500 to-purple-600' 
            : 'from-green-500 to-emerald-600'
        } rounded-xl shadow-lg p-6 text-white mb-8`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Clock className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">
                  {licenseData?.status === 'trial' 
                    ? `🎁 Prueba gratis: ${licenseData?.days_remaining || 0} días restantes`
                    : licenseData?.billing_cycle === 'lifetime'
                      ? `💎 Plan Lifetime Activo`
                      : `⏰ Próximo Pago: ${licenseData?.days_remaining || 0} días restantes`
                  }
                </h2>
                <p className="text-white/90 text-sm mt-1">
                  {licenseData?.status === 'trial'
                    ? 'Activa los módulos que necesites'
                    : licenseData?.billing_cycle === 'lifetime'
                      ? 'Sin pagos futuros - Acceso de por vida'
                      : `Plan ${licenseData?.billing_cycle === 'monthly' ? 'Mensual' : 
                                licenseData?.billing_cycle === 'semiannual' ? 'Semestral' :
                                licenseData?.billing_cycle === 'annual' ? 'Anual' : 'Lifetime'} activo`
                  }
                </p>
                <a
                  href="/tutoriales"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/70 hover:text-white text-xs mt-2 inline-block transition-colors"
                >
                  Ver Tutoriales →
                </a>
              </div>
            </div>
            
            {licenseData?.billing_cycle !== 'lifetime' && (
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end">
                  <DollarSign className="w-6 h-6" />
                  <span className="text-sm opacity-90">Total:</span>
                </div>
                <p className="text-4xl font-bold">
                  ${licenseData?.pricing?.final_monthly_total || 0}
                </p>
                <p className="text-sm opacity-90">por mes</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="space-y-4">
          {modules.map((module) => {
            const active = isActive(module.id);
            const expanded = isExpanded(module.id);
            const ModuleIcon = module.icon;
            
            // Obtener precio dinámico desde licenseData.pricing
            const currentPrice = licenseData?.pricing?.[module.id] || module.basePrice;
            
            return (
              <div
                key={module.id}
                className={`rounded-xl overflow-hidden transition-all shadow-lg ${
                  active 
                    ? `border-2 ${module.borderColor} ${isDark ? 'bg-gray-800' : 'bg-white'}`
                    : (isDark ? 'bg-gray-800 border-2 border-gray-700' : 'bg-white border-2 border-gray-300')
                }`}
              >
                <button
                  onClick={() => handleModuleClick(module)}
                  className="w-full p-6 flex items-center justify-between hover:bg-opacity-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${module.gradient} shadow-lg`}>
                      <ModuleIcon className="w-8 h-8 text-white" />
                    </div>
                    
                    <div className="text-left">
                      <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {module.name}
                      </h3>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {active ? '✓ Activo' : 'Sin activar'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {active && (
                      <div className={`${module.bgColor} text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg`}>
                        ✓ ACTIVO
                      </div>
                    )}
                    
                    {!active && (
                      <div className={`bg-gradient-to-r ${module.buttonColor} text-white px-6 py-2 rounded-lg font-semibold shadow-md`}>
                        Activar
                      </div>
                    )}
                    
                    {active && (
                      expanded 
                        ? <ChevronUp className="w-6 h-6 text-gray-400" />
                        : <ChevronDown className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                </button>
                
                {active && expanded && (
                  <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} p-4`}>
                    {module.id === 'vpn' && (
                      <div className="grid grid-cols-2 gap-4">
                        <NavButton icon={Server} label="Routers" onClick={() => navigate('/routers')} gradient="from-blue-600 to-blue-700" isDark={isDark} />
                        <NavButton icon={FileText} label="Credenciales VPN" onClick={() => navigate('/vpn-info')} gradient="from-cyan-600 to-teal-600" isDark={isDark} />
                      </div>
                    )}
                    
                    {module.id === 'hotspot' && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <NavButton icon={Server} label="Routers" onClick={() => navigate('/routers')} gradient="from-purple-600 to-purple-700" isDark={isDark} />
                        <NavButton icon={FileText} label="Planes" onClick={() => navigate('/plans')} gradient="from-red-600 to-red-700" isDark={isDark} />
                        <NavButton icon={Settings} label="Puntos de Venta" onClick={() => navigate('/points-of-sale')} gradient="from-teal-600 to-teal-700" isDark={isDark} />
                        <NavButton icon={Users} label="Vendedores" onClick={() => navigate('/sellers')} gradient="from-amber-700 to-orange-800" isDark={isDark} />
                        <NavButton icon={Printer} label="Impresión" onClick={() => navigate('/vouchers-admin')} gradient="from-rose-600 to-rose-700" isDark={isDark} />
                        <NavButton icon={DollarIcon} label="Cortes" onClick={() => navigate('/cortes')} gradient="from-indigo-800 to-purple-900" isDark={isDark} />
                        <NavButton icon={BarChart3} label="Reportes" onClick={() => navigate('/reportes')} gradient="from-pink-700 to-pink-800" isDark={isDark} />
                        <NavButton icon={CheckCircle} label="Depósitos" onClick={() => navigate('/payment-reports')} gradient="from-teal-700 to-cyan-700" isDark={isDark} />
                        <NavButton icon={Edit} label="Editor" onClick={() => navigate('/ticket-designer')} gradient="from-yellow-600 to-amber-600" isDark={isDark} />
                        <NavButton icon={FileText} label="VPN Info" onClick={() => navigate('/vpn-info')} gradient="from-cyan-600 to-cyan-700" isDark={isDark} />
                      </div>
                    )}
                    
                    {module.id === 'pppoe' && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <NavButton icon={Server} label="Routers" onClick={() => navigate('/routers')} gradient="from-green-600 to-green-700" isDark={isDark} />
                        <NavButton icon={Upload} label="Importar" onClick={() => navigate('/pppoe')} gradient="from-blue-600 to-blue-700" isDark={isDark} />
                        <NavButton icon={FileText} label="Planes" onClick={() => navigate('/pppoe/plans')} gradient="from-yellow-500 to-yellow-600" isDark={isDark} />
                        <NavButton icon={BarChart3} label="Reportes" onClick={() => navigate('/pppoe/reports')} gradient="from-orange-600 to-orange-700" isDark={isDark} />
                        <NavButton icon={Users} label="Clientes" onClick={() => navigate('/pppoe')} gradient="from-teal-600 to-cyan-600" isDark={isDark} />
                        <NavButton icon={FileText} label="VPN Info" onClick={() => navigate('/vpn-info')} gradient="from-purple-600 to-purple-700" isDark={isDark} />
                      </div>
                    )}
                    
                    {module.id === 'tools' && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <NavButton icon={Wrench} label="Portales Hotspot" onClick={() => navigate('/portal-designer')} gradient="from-orange-600 to-amber-600" isDark={isDark} />
                        <NavButton icon={FileText} label="Generador Scripts" onClick={() => navigate('/script-generator')} gradient="from-red-600 to-rose-600" isDark={isDark} />
                        <NavButton icon={Settings} label="Solución MAC" onClick={() => navigate('/mac-solution')} gradient="from-amber-600 to-yellow-600" isDark={isDark} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {showModal && selectedModule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-md w-full p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {selectedModule.name}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Usar este servicio tendrá un costo de:{' '}
              <span className="font-bold text-blue-600 text-2xl">${selectedModule.basePrice}</span>
              {selectedModule.isOneTime ? ' pago único' : ' mensual'}
            </p>
            
            <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {selectedModule.isOneTime 
                ? `Este paquete tiene un costo de $${selectedModule.basePrice}. Una vez que reportes tu pago, podrás acceder.`
                : 'Será cargado en tu próximo pago al finalizar tu período de prueba.'
              }
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                disabled={activating}
                className={`flex-1 px-4 py-3 rounded-lg font-semibold ${
                  isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                Cancelar
              </button>
              <button
                onClick={handleActivate}
                disabled={activating}
                className={`flex-1 bg-gradient-to-r ${selectedModule.buttonColor} text-white px-4 py-3 rounded-lg font-semibold hover:shadow-lg transition-all`}
              >
                {activating ? 'Activando...' : (selectedModule.isOneTime ? 'Comprar' : 'Activar')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Popup de Anuncios */}
      {announcements.length > 0 && (
        <AnnouncementPopup
          announcement={announcements[currentAnnouncementIndex]}
          onDismiss={handleDismissAnnouncement}
          theme={isDark ? "dark" : "light"}
        />
      )}
    </div>
  );
};

const NavButton = ({ icon: Icon, label, onClick, gradient, isDark }) => {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-2 p-4 rounded-lg transition-all transform hover:scale-105 shadow-md bg-gradient-to-br ${gradient} hover:shadow-xl text-white`}
    >
      <Icon className="w-7 h-7" />
      <span className="text-xs font-semibold text-center leading-tight">{label}</span>
    </button>
  );
};

export default DashboardPage;
